import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ComplianceReportListResponse,
  ComplianceReportListParams,
  ComplianceReportDetailDto,
  ComplianceReportDto,
  GenerateReportResult,
  AmendReportRequest,
  AmendReportResult,
  GenerateReportRequest,
  FilePackageRequest,
  ReportPreviewResponse,
  RecordReceiptRequest,
  DismissReceiptChaseRequest,
  ReportBeginningBalanceRequest,
  ReportFinalReportReasonRequest,
  AmendmentModel,
  FilingPreviewDto,
  PreflightResult,
  SigningEvidenceDto,
  FilingChainDto,
  EffectivePreviewDto,
  WarningDispositionInput,
  PeriodLockMutationRequest,
  PeriodLockMutationResult,
  MarkExportBatchUploadedRequest,
  MarkExportBatchUploadedResultDto,
  TransactionResendKind,
  TransactionResendRequest,
  TransactionResendResultDto,
  MarkFiledInPortalRequest,
  MarkFiledInPortalResultDto,
  MarkFiledPreviewDto,
  ReportReopenResult,
  ReopenAdvisory,
  PreSessionReportPreview,
  PreSessionSessionDateConfirmResult,
  RapidReportMarkReportedResponse,
  DownstreamCashRecalcPreview,
} from '@signalvote/shared';
import { ApiFailure, ErrorCode, failureFromResponse, isStaleWrite } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { API_BASE_URL } from '../../config';
import { apiJson, apiResponse, type ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { campaignHeaders, defineQuery, defineWrite, edit } from '../../lib/data';
import { skip, useRefresh, type CurrentRead } from '@signalvote/data';
import { useCampaignStore } from '../../stores';
import { FINANCE_STALE_TIME } from './queryUtils';

/**
 * The error most report mutations throw on a non-ok response: the server's sentence (`error` on a
 * refusal, `message` on a body with no code), else the fallback — never the `code`. `??`, not `||`:
 * an empty sentence is kept. `body` is null when the response carried a JSON `null`.
 */
function sentenceOr(fallback: string) {
  return (body: ApiErrorBody | null) => new Error(body?.error ?? body?.message ?? fallback);
}

// Re-export types for consumer convenience
export type {
  ComplianceReportListResponse,
  ComplianceReportListParams,
  ComplianceReportDetailDto,
  GenerateReportResult,
  ReportPreviewResponse,
};

// ============================================================================
// Cross-period cash recalc preview (issue #643)
// ============================================================================

/**
 * Warn-before-confirm data for the cross-period cash recalc: given the date a
 * cash-affecting edit would land, which later EDITABLE periods would have their
 * opening balance re-seeded, and which later FILED periods would be left
 * inconsistent (a portal-amendment matter). Read-only — the recalc itself runs
 * synchronously as part of the confirmed mutation. Pass `null` to disable.
 */
const cashRecalcPreview = defineQuery({
  key: (date: string) => ['finance', 'reports', 'cash-recalc-preview', date],
  reads: 'GET /api/finance/reports/cash-recalc-preview',
  // The named later periods must stay fresh as earlier-period edits land.
  staleTime: 0,
  fetch: (date, { signal, campaignId }) =>
    apiJson<DownstreamCashRecalcPreview>(`/api/finance/reports/cash-recalc-preview?date=${date}`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch cash recalc preview',
    }),
});

export function useCashRecalcPreview(date: string | null): CurrentRead<DownstreamCashRecalcPreview> {
  // The preview requires Finance Do; below that the server only answers 403.
  const canDoFinance = useCampaignStore((s) => s.canDo('Finance'));
  return cashRecalcPreview(date && canDoFinance ? date : skip);
}

// ============================================================================
// Report Preview (FIP-008)
// ============================================================================

const reportPreview = defineQuery({
  key: (deadlineId: string) => ['finance', 'reports', 'preview', deadlineId],
  reads: 'GET /api/finance/reports/preview',
  staleTime: 30_000,
  fetch: async (deadlineId, { signal, campaignId }): Promise<ReportPreviewResponse | null> => {
    const response = await apiResponse(`/api/finance/reports/preview?deadlineId=${deadlineId}`, {
      signal,
      headers: campaignHeaders(campaignId),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch report preview');
    return response.json() as Promise<ReportPreviewResponse>;
  },
});

/** The period's report as it would generate now; `null` when the deadline isn't in the campaign. */
export function useReportPreview(deadlineId: string | null): CurrentRead<ReportPreviewResponse | null> {
  return reportPreview(deadlineId ?? skip);
}

// ============================================================================
// Report List
// ============================================================================

function buildReportListPath(params: ComplianceReportListParams): string {
  const searchParams = new URLSearchParams();
  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.pageSize != null) searchParams.set('pageSize', String(params.pageSize));
  if (params.status) searchParams.set('status', params.status);
  if (params.deadlineId) searchParams.set('deadlineId', params.deadlineId);
  return `/api/finance/reports?${searchParams.toString()}`;
}

/** A page of the campaign's reports, newest period first. */
export const useFinanceReports = defineQuery({
  key: (params: ComplianceReportListParams) => ['finance', 'reports', 'list', params],
  reads: 'GET /api/finance/reports',
  fetch: (params, { signal, campaignId }) =>
    apiJson<ComplianceReportListResponse>(buildReportListPath(params), {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch reports',
    }),
  staleTime: FINANCE_STALE_TIME,
});

// ============================================================================
// Report Detail
// ============================================================================

/** A report with its line items, totals and warnings, computed live; `null` when it isn't in the current campaign. */
export const useFinanceReportDetail = defineQuery({
  key: (id: string) => ['finance', 'reports', 'detail', id],
  reads: 'GET /api/finance/reports/{id}',
  fetch: async (id, { signal, campaignId }): Promise<ComplianceReportDetailDto | null> => {
    const response = await apiResponse(`/api/finance/reports/${id}`, { signal, headers: campaignHeaders(campaignId) });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch report detail');
    return response.json() as Promise<ComplianceReportDetailDto>;
  },
});

/** Shows the report with `fields` changed, before the server answers. */
function editDetail(id: string, fields: Partial<ComplianceReportDetailDto>) {
  return [edit(useFinanceReportDetail, id, (report) => report && { ...report, ...fields })];
}

/** Throws a report write's non-ok answer with the server's sentence or `fallback`; its code decides a stale write from a refusal. */
async function throwReportWriteError(response: Response, fallback: string): Promise<never> {
  if (response.status === 404) throw new ApiFailure(404, null, 'Report not found');
  const err = (await response.json().catch(() => null)) as Record<string, unknown> | null;
  const code = typeof err?.code === 'string' ? err.code : null;
  const message = (err?.error as string | undefined) ?? (err?.message as string | undefined) ?? fallback;
  throw new ApiFailure(response.status, code, message, null, err ?? {});
}

// ============================================================================
// Generate Report
// ============================================================================

const useGenerateReportWrite = defineWrite({
  send: async (request: GenerateReportRequest) => {
    const response = await apiResponse('/api/finance/reports/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      json: request,
    });
    if (response.status === 404) throw new Error('Deadline not found');
    if (response.status === 409) {
      // 409 now spans two guards: the duplicate-report guard and the ADR-064 sequential
      // file-order guard. The latter's message names the earlier period to close first, so
      // surface it verbatim; otherwise keep the duplicate copy. (Guarded read so a body-less
      // 409 still throws the duplicate message.)
      const err = response.json ? await response.json().catch(() => null) : null;
      if (err?.code === ErrorCode.PriorPeriodNotClosed) {
        throw new Error(err.error ?? 'Close the earlier reporting period first, then generate this one.');
      }
      throw new Error('Report already exists for this deadline');
    }
    if (!response.ok) throw new Error('Failed to generate report');
    return response.json() as Promise<GenerateReportResult>;
  },
});

export function useGenerateReport() {
  const { mutateAsync } = useGenerateReportWrite();
  return { generateReport: mutateAsync };
}

// ============================================================================
// Generate Whole Report (#126) — the non-finalizing whole-document generate
// seam for a WholeReport + uploadIsFiling=false jurisdiction (VA). POST
// /reports/{id}/generate builds the report's SubmissionPackage and streams the
// XML for upload to the state portal WITHOUT finalizing: no Confirmed→Reported
// flip, no Status=Filed, no cash freeze (generate ≠ file, ADR-019). Repeatable
// and free. The treasurer uploads to COMET, then finalizes via
// `mark-filed-in-portal`. Distinct from `useGenerateReport` (which CREATES a
// report from a deadline) and from `useExportDelta` (AL incremental delta).
// ============================================================================

export interface GenerateWholeReportResult {
  fileBlob: Blob;
  fileName: string;
  submissionPackageId: string | null;
}

/**
 * #313 — a typed whole-report-generate failure carrying the server's stable `code` so the
 * generate button can branch on it (e.g. the missing-State-Filer-ID config error → a Finance
 * Settings link) instead of string-matching the human message. Mirrors `MarkFiledInPortalError` /
 * `ObligationGenerateError`. `code` is null when the body carried no structured code (a non-JSON
 * plain-string body — see `readGenerateWholeReportError`).
 */
export class GenerateWholeReportError extends Error {
  constructor(public readonly code: string | null, message: string) {
    super(message);
    this.name = 'GenerateWholeReportError';
  }
}

/**
 * #313 — parse a non-ok generate response into a typed error WITHOUT losing the specific message.
 * The endpoint returns a structured JSON 400 (`{ error, code }`, the sentence in `error`) for its named
 * refusals (the NC missing-filer-id case is `MapSubmissionPackageException` →
 * `{ code: 'MissingStateFilerId', … }`), so the JSON path preserves the stable code + sentence. But a body that is NOT JSON must
 * still surface verbatim rather than collapse to the generic fallback (the epic's live-path rule),
 * so we read the body as text once and fall back to the raw string when it doesn't parse.
 */
async function readGenerateWholeReportError(response: Response): Promise<GenerateWholeReportError> {
  const raw = await response.text().catch(() => '');
  let code: string | null = null;
  let message: string | null = null;
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === 'object') {
        const body = parsed as { error?: unknown; code?: unknown; message?: unknown };
        code = typeof body.code === 'string' ? body.code : null;
        message =
          typeof body.error === 'string'
            ? body.error
            : typeof body.message === 'string'
              ? body.message
              : null;
      } else if (typeof parsed === 'string') {
        // A JSON-encoded bare string body (some controllers return BadRequest(string)).
        message = parsed;
      }
    } catch {
      // Non-JSON plain-string body — keep it verbatim so the specific message survives.
      message = raw;
    }
  }
  return new GenerateWholeReportError(code, message ?? 'Failed to generate the report');
}

const useGenerateWholeReportWrite = defineWrite({
  send: async (reportId: string): Promise<GenerateWholeReportResult & { reportId: string }> => {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}/api/finance/reports/${reportId}/generate`,
      { method: 'POST' },
    );
    if (!response.ok) {
      throw await readGenerateWholeReportError(response);
    }
    const fileBlob = await response.blob();
    // Jurisdiction-neutral fallback (matches the sibling download hooks). The real
    // filename comes from Content-Disposition; this only fires if the server omits it.
    const fallback = `${reportId}-report.xml`;
    const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
    return {
      fileBlob,
      fileName,
      submissionPackageId: response.headers.get('X-Submission-Package-Id'),
      reportId,
    };
  },
});

export function useGenerateWholeReport() {
  const { mutateAsync } = useGenerateWholeReportWrite();

  return { generateWholeReport: mutateAsync };
}

// ============================================================================
// File Report (FIN-010) — endpoint returns an application/zip body plus
// X-Amendment-Model and X-Submission-Package-Id headers. Callers receive a
// structured result so the UI can both trigger a browser download and surface
// the amendment hint. ADR-021 — the header carries the ONE ruleset-declared
// amendment axis (federal = ElectronicResubmit, distinct from LA/VA's
// SelfContainedUpload), replacing the retired X-Amendment-Workflow header.
// ============================================================================

export interface FileReportResult {
  zipBlob: Blob;
  fileName: string;
  amendmentModel: AmendmentModel | null;
  submissionPackageId: string | null;
}

function readDispositionFileName(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  // Match RFC 5987 filename* first (UTF-8 percent-encoded), fall back to plain filename=.
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
  const plain = /filename=("?)([^";]+)\1/i.exec(disposition);
  if (plain?.[2]) return plain[2].trim();
  return fallback;
}

const useFileReportWrite = defineWrite({
  row: 'ComplianceReport',
  send: async (
    reportId,
    concurrencyVersion: number,
    { data }: { data: FilePackageRequest },
  ): Promise<FileReportResult & { reportId: string; concurrencyVersion: number }> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/file`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...data, concurrencyVersion }),
        },
      );
      if (!response.ok) {
        // Service-side errors carry { error, code, payload? } — surface the
        // human sentence (`error`) when present so the UI can render an actionable toast.
        const err = await response.json().catch(() => null);
        if (isStaleWrite(err)) throw failureFromResponse(response, err);
        // PPR-009: the unreconciled-contributions guard returns a structured
        // payload the modal must render — typed exception so the catch block
        // can switch on error code without parsing.
        if (err?.code === ErrorCode.UnconfirmedContributionsInPeriod) {
          throw new FileReportUnconfirmedContributionsError(
            err.error ?? 'Unreconciled contributions blocking this filing.',
            (err.payload?.contributionIds as string[] | undefined) ?? [],
            (err.payload?.count as number | undefined) ?? 0,
          );
        }
        // FPD-002: attestation-shape failures keep the modal open with the
        // server message; other errors close it and bubble through.
        if (err?.code === ErrorCode.AttestationMissing
            || err?.code === ErrorCode.AttestationNameMismatch
            || err?.code === ErrorCode.AttestationReasonMissing) {
          throw new FileReportAttestationError(
            err.code,
            err.error ?? 'Attestation validation failed. Re-confirm and retry.',
          );
        }
        // §25 — no in-system package to generate (transactions already uploaded via
        // Export / period filed in the portal). Typed so the modal can soften it.
        if (err?.code === ErrorCode.ExternalPortalReport) {
          throw new FileReportPortalNoPackageError(
            err.error ?? 'This period has no in-system package to generate.',
          );
        }
        throw new Error(err?.error ?? err?.message ?? 'Failed to file report');
      }
      const zipBlob = await response.blob();
      const fallback = `report-${reportId}.zip`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      const modelHeader = response.headers.get('X-Amendment-Model') as AmendmentModel | null;
      return {
        zipBlob,
        fileName,
        amendmentModel: modelHeader,
        submissionPackageId: response.headers.get('X-Submission-Package-Id'),
        reportId,
        // The body is the zip, so the report's new token rides a header.
        concurrencyVersion: Number(response.headers.get('X-Report-Concurrency-Version')),
      };
  },
  land: (result, { row }) => editDetail(row.id, { concurrencyVersion: result.concurrencyVersion }),
});

/**
 * Files the report. The detail, list, health, chain and filing preview refresh from the write set.
 */
export function useFileReport() {
  const { mutateAsync } = useFileReportWrite();
  const fileReport = useCallback(
    ({ reportId, data }: { reportId: string; data: FilePackageRequest & { concurrencyVersion: number } }) =>
      mutateAsync({ row: { id: reportId, concurrencyVersion: data.concurrencyVersion }, data }),
    [mutateAsync],
  );
  return { fileReport };
}

// ============================================================================
// Download Package (FIN-011) — GET /reports/{id}/package returns the same
// shape of zip as POST /file. Used by the "Download Package" button on
// filed-report cards.
// ============================================================================

export function useDownloadReportPackage() {
  const { mutateAsync } = useMutation({
    mutationFn: async (reportId: string): Promise<FileReportResult> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/package`,
      );
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? 'Failed to download package');
      }
      const zipBlob = await response.blob();
      const fallback = `report-${reportId}.zip`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return {
        zipBlob,
        fileName,
        amendmentModel: null,
        submissionPackageId: null,
      };
    },
  });

  return { downloadPackage: mutateAsync };
}

// ============================================================================
// Download CRO-1000 cover (ADR-054) — GET /reports/{id}/cover returns the
// signable CRO-1000 cover PDF as a distinct affordance from the package zip.
// North Carolina only (404 elsewhere); surfaced on the post-file panel so the
// treasurer can get the cover, sign it, and send it.
// ============================================================================

export interface CoverDownloadResult {
  blob: Blob;
  fileName: string;
}

export function useDownloadReportCover() {
  const { mutateAsync } = useMutation({
    mutationFn: async (reportId: string): Promise<CoverDownloadResult> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/cover`,
      );
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? 'Failed to download cover');
      }
      const blob = await response.blob();
      const fallback = `cro-1000-cover-${reportId}.pdf`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return { blob, fileName };
    },
  });

  return { downloadCover: mutateAsync };
}

// ============================================================================
// Export new AL transactions (ADR-020) — POST /reports/{id}/export-delta builds
// the incremental EDI file of the period's NOT-yet-uploaded Confirmed rows. The
// AL FCPA portal is append-only, so each transaction uploads exactly once. The
// endpoint returns either an application/xml body (the delta file, with an
// X-Export-Batch-Id header) or a 200 JSON `{ exported: false }` when there's
// nothing new to export.
// ============================================================================

export type ExportDeltaResult =
  | { exported: true; fileBlob: Blob; fileName: string; exportBatchId: string | null }
  | { exported: false; message: string };

const useExportDeltaWrite = defineWrite({
  send: async (reportId: string): Promise<ExportDeltaResult & { reportId: string }> => {
    const response = await authService.authenticatedFetch(
      `${API_BASE_URL}/api/finance/reports/${reportId}/export-delta`,
      { method: 'POST' },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error ?? err?.message ?? 'Failed to export new transactions');
    }
    // An empty delta returns a JSON no-op; a real delta returns the EDI file.
    const contentType = response.headers.get('Content-Type') ?? '';
    if (contentType.includes('application/json')) {
      const body = (await response.json().catch(() => null)) as
        | { exported?: boolean; message?: string }
        | null;
      return {
        exported: false,
        message: body?.message ?? 'There are no new transactions to export.',
        reportId,
      };
    }
    const fileBlob = await response.blob();
    // Jurisdiction-neutral fallback (matches the sibling download hooks). The real
    // filename comes from Content-Disposition; this only fires if the server omits it.
    const fallback = `${reportId}-new-transactions.xml`;
    const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
    return {
      exported: true,
      fileBlob,
      fileName,
      exportBatchId: response.headers.get('X-Export-Batch-Id'),
      reportId,
    };
  },
});

export function useExportDelta() {
  const { mutateAsync } = useExportDeltaWrite();

  return { exportDelta: mutateAsync };
}

// ============================================================================
// Request a single-transaction re-send (#645/#647) — POST
// /api/finance/transaction-resends records a deliberate, audited re-send of ONE already-sent
// transaction the state lost (or that the treasurer deleted in the portal). The AL FCPA portal is
// append-only (ADR-020), so the automatic export never re-emits a sent row; this is the one
// sanctioned exception. The next export force-includes exactly that transaction once. Invalidates the
// report detail so the row's server-computed `resendQueued` flag refreshes to true (the action then
// reads "re-send queued" and disables — re-sends can't be piled up).
// ============================================================================

const useRequestTransactionResendWrite = defineWrite({
  send: async ({
    reportId: _reportId,
    kind,
    transactionId,
  }: {
    // Not sent: the API body is just { kind, transactionId } (the re-send is keyed on the
    // transaction, not the report). The report detail refreshes from the write set.
    reportId: string;
    kind: TransactionResendKind;
    transactionId: string;
  }): Promise<TransactionResendResultDto> => {
    const request: TransactionResendRequest = { kind, transactionId };
    return apiJson<TransactionResendResultDto>('/api/finance/transaction-resends', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      json: request,
      error: sentenceOr('Failed to queue the re-send'),
    });
  },
});

export function useRequestTransactionResend() {
  const { mutateAsync } = useRequestTransactionResendWrite();

  return { requestResend: mutateAsync };
}

// ============================================================================
// Mark export uploaded (ADR-020) — answer the §4.1 acceptance gate. POST
// /export-batches/{id}/mark-uploaded with `{ accepted }`. "Accepted" asks whether
// the AL FCPA portal IMPORTED the file (not whether you clicked upload) — the portal
// is the only validator, so a yes-to-a-rejected-file would silently drop the rows.
// Yes flips the draft's member rows Confirmed → Reported; No discards the draft and
// returns the rows to the not-yet-uploaded pool. A stale answer (the draft is no
// longer Generated) is a 409.
// ============================================================================

const useMarkExportBatchUploadedWrite = defineWrite({
  send: async ({
    exportBatchId,
    accepted,
  }: {
    exportBatchId: string;
    accepted: boolean;
    // reportId rides along only so onSuccess can target the detail query — the
    // banner lives on the report-detail read.
    reportId: string;
  }): Promise<MarkExportBatchUploadedResultDto> => {
    return apiJson<MarkExportBatchUploadedResultDto>(
      `/api/finance/export-batches/${exportBatchId}/mark-uploaded`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        json: { accepted } satisfies MarkExportBatchUploadedRequest,
        error: sentenceOr('Failed to confirm the export upload'),
      },
    );
  },
});

export function useMarkExportBatchUploaded() {
  const { mutateAsync } = useMarkExportBatchUploadedWrite();

  return { markExportBatchUploaded: mutateAsync };
}

// ============================================================================
// Mark filed in portal (ADR-023) — POST /reports/{id}/mark-filed-in-portal. The
// treasurer attests the PERIOD REPORT was filed in the portal (upload ≠ file). This is
// now a COMMITTING attestation: it sets Status = Filed + FilingOrigin = PortalAttested
// AND flips the report's period `Confirmed` rows `Confirmed → Reported` in the same
// SaveChanges. The confirmation number is optional; a stale concurrencyVersion is a 409.
// New 409 codes: `MarkFiledPendingExportMustResolveFirst` (a live Generated export draft
// must be resolved first) and `MarkFiledAlreadyFiled` (should be gated away — Reopen is
// the only forward move once filed). Both surface as a typed `MarkFiledInPortalError`.
// ============================================================================

/**
 * ADR-023 — a typed mark-filed failure carrying the server's stable `code` so the
 * button can map it to specific treasurer copy (the pending-export block, the
 * already-filed edge) instead of parsing the message.
 */
export class MarkFiledInPortalError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'MarkFiledInPortalError';
  }
}

const useMarkFiledInPortalWrite = defineWrite({
  row: 'ComplianceReport',
  send: async (reportId, concurrencyVersion: number, { data }: { data: MarkFiledInPortalRequest }) => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/mark-filed-in-portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      json: { ...data, concurrencyVersion },
    });
    if (response.status === 404) throw new Error('Report not found');
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      if (isStaleWrite(err)) throw failureFromResponse(response, err);
      // Preserve the stable error code so the button can render the code-specific
      // copy (pending-export must-resolve, already-filed). Falls back to a plain
      // Error only when the server sent no code.
      if (err?.code) {
        throw new MarkFiledInPortalError(err.code as string, err.error ?? 'Failed to mark the report filed');
      }
      throw new Error(err?.error ?? err?.message ?? 'Failed to mark the report filed');
    }
    const result = (await response.json()) as MarkFiledInPortalResultDto;
    return { ...result, concurrencyVersion: result.report.concurrencyVersion };
  },
  land: (result, { row }) => editDetail(row.id, { concurrencyVersion: result.concurrencyVersion }),
});

/**
 * Status = Filed and the period's rows flipped to Reported. The detail, list, deadlines, health and
 * filing chain refresh from the write set.
 */
export function useMarkFiledInPortal() {
  const { mutateAsync } = useMarkFiledInPortalWrite();
  const markFiledInPortal = useCallback(
    ({ reportId, data }: { reportId: string; data: MarkFiledInPortalRequest & { concurrencyVersion: number } }) =>
      mutateAsync({ row: { id: reportId, concurrencyVersion: data.concurrencyVersion }, data }),
    [mutateAsync],
  );
  return { markFiledInPortal };
}

// ============================================================================
// Mark-filed preview (ADR-023) — GET /reports/{id}/mark-filed-preview (View perm). The
// pre-confirm count the always-confirm dialog renders: how many `Confirmed` rows the
// attestation will commit to `Reported`. Fetched only while the mark-filed action is on
// screen (the button self-gates); returns null when the report is out of the campaign.
// ============================================================================

const markFiledPreviewQuery = defineQuery({
  key: (reportId: string) => ['finance', 'reports', 'mark-filed-preview', reportId],
  reads: 'GET /api/finance/reports/{id}/mark-filed-preview',
  // The count must match the report's live Confirmed set at confirm time.
  staleTime: 0,
  fetch: async (reportId, { signal, campaignId }): Promise<MarkFiledPreviewDto | null> => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/mark-filed-preview`, {
      signal,
      headers: campaignHeaders(campaignId),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch the mark-filed preview');
    return response.json() as Promise<MarkFiledPreviewDto>;
  },
});

export function useMarkFiledPreview(reportId: string | null): CurrentRead<MarkFiledPreviewDto | null> {
  return markFiledPreviewQuery(reportId ?? skip);
}

// ============================================================================
// Reopen report (ADR-023) — POST /reports/{id}/reopen (Manage perm). The symmetric undo
// of "mark filed in portal": reverts Status → Ready, clears the frozen as-filed balances
// + FilingOrigin + deadline lock, and reverts the rows the attestation committed
// (Reported → Confirmed), leaving genuinely-uploaded ExportBatch members Reported. Body
// echoes `report.concurrencyVersion` (never hardcode 0). Only `filedWithState: false`
// reopens. Errors: ReopenReportNotFound (404), ReopenReportNotReopenable (400),
// ReopenReportFiledWithStateUseUnlock (400, a yes or no answer: use the period unlock),
// ReopenReportConcurrencyMismatch (409).
// ============================================================================

// ADR-064 — the amendment-first / attest-then-reopen advisory for a Closed period. Read-only
// guidance the "change this closed period" affordance fetches to decide whether to lead with
// amendment (a filing is on record) or reopen (an in-app-only no-activity close — a clean undo),
// and to surface the later-Closed-period warning before the treasurer commits. staleTime=0 so the
// decision is fresh each open; `enabled` lets the affordance defer the fetch until it's needed.
const reopenAdvisoryQuery = defineQuery({
  key: (reportId: string) => ['finance', 'reports', 'reopen-advisory', reportId],
  reads: 'GET /api/finance/reports/{id}/reopen-advisory',
  staleTime: 0,
  fetch: (reportId, { signal, campaignId }) =>
    apiJson<ReopenAdvisory>(`/api/finance/reports/${reportId}/reopen-advisory`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to load reopen advisory',
    }),
});

export function useReopenAdvisory(reportId: string, enabled = true): CurrentRead<ReopenAdvisory> {
  return reopenAdvisoryQuery(enabled && reportId ? reportId : skip);
}

const useReopenReportWrite = defineWrite({
  row: 'ComplianceReport',
  send: async (
    reportId,
    concurrencyVersion: number,
    // ADR-064 attest-then-reopen: the treasurer's "have you actually filed?" answer. Omitted =
    // not answered; false = clean reopen; true = still reopens (alert-don't-block) but warns.
    { filedWithState }: { filedWithState?: boolean | null },
  ) => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/reopen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      json: { concurrencyVersion, filedWithState },
    });
    if (!response.ok) await throwReportWriteError(response, 'Failed to reopen the report');
    const result = (await response.json()) as ReportReopenResult;
    return { ...result, concurrencyVersion: result.report.concurrencyVersion };
  },
  land: (result, { row }) => editDetail(row.id, { concurrencyVersion: result.concurrencyVersion }),
});

/**
 * Status → Ready and the attested rows reverted. The detail, list, deadlines, health, reopen
 * advisory and filing chain refresh from the write set.
 */
export function useReopenReport() {
  const { mutateAsync } = useReopenReportWrite();
  const reopenReport = useCallback(
    ({ reportId, concurrencyVersion, filedWithState }: {
      reportId: string;
      concurrencyVersion: number;
      filedWithState?: boolean | null;
    }) =>
      mutateAsync({ row: { id: reportId, concurrencyVersion }, filedWithState }),
    [mutateAsync],
  );
  return { reopenReport };
}

// ============================================================================
// FPD-026 — Download Audit Packet. Sibling to useDownloadReportPackage but
// targets the FPD-014 audit-packet ZIP endpoint and surfaces typed HTTP
// status discriminators so the consumer can render the AC's literal copy
// per error class without parsing the body.
// ============================================================================

export type AuditPacketDownloadStatusKind = 'Forbidden' | 'NotFound' | 'ServerError';

export class AuditPacketDownloadError extends Error {
  constructor(public readonly kind: AuditPacketDownloadStatusKind) {
    super(`AuditPacketDownload:${kind}`);
    this.name = 'AuditPacketDownloadError';
  }
}

export interface AuditPacketDownloadResult {
  zipBlob: Blob;
  fileName: string;
}

export function useDownloadAuditPacket() {
  const { mutateAsync } = useMutation({
    mutationFn: async (reportId: string): Promise<AuditPacketDownloadResult> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/reports/${reportId}/audit-packet.zip`,
      );
      if (!response.ok) {
        // Per the AC, 403 / 404 / 5xx each map to a distinct toast literal.
        // The hook surfaces the kind; the component owns copy lookup so we
        // don't bake strings into the data layer.
        if (response.status === 403) throw new AuditPacketDownloadError('Forbidden');
        if (response.status === 404) throw new AuditPacketDownloadError('NotFound');
        throw new AuditPacketDownloadError('ServerError');
      }
      const zipBlob = await response.blob();
      // FPD-026 ticket Technical Notes: filename comes from the
      // Content-Disposition header set server-side; the frontend doesn't
      // compute it. Fallback is a regression guard.
      const fallback = `audit-packet-${reportId}.zip`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return { zipBlob, fileName };
    },
  });

  return { downloadAuditPacket: mutateAsync };
}

// ============================================================================
// Record Receipt (FIN-011) — PATCH /reports/{id}/receipt for treasurer-entered
// state filing receipt.
// ============================================================================

const useRecordReceiptWrite = defineWrite({
  row: 'ComplianceReport',
  send: async (reportId, concurrencyVersion: number, { data }: { data: RecordReceiptRequest }) => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/receipt`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      json: { ...data, concurrencyVersion },
    });
    if (!response.ok) await throwReportWriteError(response, 'Failed to record receipt');
    return response.json() as Promise<ComplianceReportDto & { id: string }>;
  },
  // The receipt fields are known on click (clearing = both empty → null).
  patch: ({ row, data }) =>
    editDetail(row.id, {
      stateFilingReceiptId: data.stateFilingReceiptId || null,
      stateFilingReceiptAt: data.stateFilingReceiptAt,
    }),
  land: (result, { row }) => editDetail(row.id, { concurrencyVersion: result.concurrencyVersion }),
});

export function useRecordReceipt() {
  const { mutateAsync } = useRecordReceiptWrite();
  const recordReceipt = useCallback(
    ({ reportId, data }: { reportId: string; data: RecordReceiptRequest & { concurrencyVersion: number } }) =>
      mutateAsync({ row: { id: reportId, concurrencyVersion: data.concurrencyVersion }, data }),
    [mutateAsync],
  );
  return { recordReceipt };
}

// ============================================================================
// Dismiss the receipt chase — POST /reports/{id}/receipt-chase/dismiss. The treasurer's recorded
// decision to stop waiting for the receiving office's confirmation of receipt (proofOfFiling
// jurisdictions — MS). Same optimistic-token recipe as the receipt edit; the open-item flag itself is
// server-derived from the recorded decision, so it's patched closed here and reconciled on settle.
// ============================================================================

const useDismissReceiptChaseWrite = defineWrite({
  row: 'ComplianceReport',
  send: async (reportId, concurrencyVersion: number) => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/receipt-chase/dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      json: { concurrencyVersion } satisfies DismissReceiptChaseRequest,
    });
    if (!response.ok) await throwReportWriteError(response, 'Failed to dismiss the receipt reminder');
    return response.json() as Promise<ComplianceReportDto & { id: string }>;
  },
  patch: ({ row }) => editDetail(row.id, { receiptChaseOpen: false }),
  land: (result, { row }) => editDetail(row.id, { concurrencyVersion: result.concurrencyVersion }),
});

export function useDismissReceiptChase() {
  const { mutateAsync } = useDismissReceiptChaseWrite();
  const dismissReceiptChase = useCallback(
    ({ reportId, concurrencyVersion }: { reportId: string; concurrencyVersion: number }) =>
      mutateAsync({ row: { id: reportId, concurrencyVersion } }),
    [mutateAsync],
  );
  return { dismissReceiptChase };
}

// ============================================================================
// Override Beginning Balance (FCG-009) — PATCH /reports/{id}/beginning-balance.
// Treasurer adjusts the defaulted beginning balance (sourced from the prior
// period's as-filed snapshot) before filing. concurrencyVersion is threaded
// from the report detail DTO; a stale version is a 409.
// ============================================================================

const useOverrideBeginningBalanceWrite = defineWrite({
  row: 'ComplianceReport',
  send: async (reportId, concurrencyVersion: number, { data }: { data: ReportBeginningBalanceRequest }) => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/beginning-balance`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      json: { ...data, concurrencyVersion },
    });
    if (!response.ok) await throwReportWriteError(response, 'Failed to update beginning balance');
    return response.json() as Promise<ComplianceReportDto & { id: string }>;
  },
  // Only the opening balance is known on click; the write set's refetch brings the recomputed cash.
  patch: ({ row, data }) => editDetail(row.id, { beginningBalance: data.beginningBalance }),
  land: (result, { row }) => editDetail(row.id, { concurrencyVersion: result.concurrencyVersion }),
});

export function useOverrideBeginningBalance() {
  const { mutateAsync } = useOverrideBeginningBalanceWrite();
  const overrideBeginningBalance = useCallback(
    ({ reportId, data }: { reportId: string; data: ReportBeginningBalanceRequest & { concurrencyVersion: number } }) =>
      mutateAsync({ row: { id: reportId, concurrencyVersion: data.concurrencyVersion }, data }),
    [mutateAsync],
  );
  return { overrideBeginningBalance };
}

/**
 * GitHub #778 — mark a report the committee's FINAL report (or clear it). A non-null
 * `finalReportReason` marks it final (for LA, checks the matching CVR box); null clears it.
 * Mirrors {@link useOverrideBeginningBalance}: plain `mutateAsync` + refetch (this is a rare,
 * deliberate click, not a latency-sensitive one, so no optimistic cache surgery). The
 * `concurrencyVersion` in the body is the guard — a stale token is a 409. Reject a locked-period
 * write (400) or a stale token (409) with a treasurer message.
 */
const useSetFinalReportReasonWrite = defineWrite({
  send: async ({
    reportId,
    data,
  }: { reportId: string; data: ReportFinalReportReasonRequest }) => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/final-report-reason`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      json: data,
    });
    if (response.status === 404) throw new Error('Report not found');
    if (response.status === 409) {
      throw new Error('This report changed since you loaded it. Refresh and try again.');
    }
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.error ?? err?.message ?? 'Failed to update the final-report flag');
    }
    return response.json() as Promise<ComplianceReportDto & { id: string }>;
  },
});

export function useSetFinalReportReason() {
  const { mutateAsync } = useSetFinalReportReasonWrite();

  return { setFinalReportReason: mutateAsync };
}

// ============================================================================
// Filing Preflight (PPR-009)
// GET /reports/{id}/preview returns the dataset the modal renders. The
// `error` on the parsed payload (when present) carries the
// UnconfirmedContributionsInPeriod stable code thrown by FileReportAsync,
// so the same hook can be reused for the post-confirm 400 fallback.
// ============================================================================

export class FileReportUnconfirmedContributionsError extends Error {
  readonly contributionIds: string[];
  readonly count: number;
  constructor(message: string, contributionIds: string[], count: number) {
    super(message);
    this.name = 'FileReportUnconfirmedContributionsError';
    this.contributionIds = contributionIds;
    this.count = count;
  }
}

/**
 * §25 — filing was refused because the report has no in-system package to
 * generate: its transactions were already uploaded via Export (PortalAttested)
 * or the period was filed directly in the portal (ExternalPortal). Server error
 * code <code>ExternalPortalReport</code>. Typed so the preflight modal can render
 * a soft, non-alarming explanation instead of the raw red service error when the
 * report is still Ready (the export-confirm dead-end).
 */
export class FileReportPortalNoPackageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FileReportPortalNoPackageError';
  }
}

/**
 * FPD-002 — server-side attestation validation failed
 * (<code>AttestationMissing</code>, <code>AttestationNameMismatch</code>, or
 * <code>AttestationReasonMissing</code>). The attestation modal keeps itself
 * open and renders the message so the treasurer can retry without
 * re-running preflight.
 */
type AttestationErrorCode =
  | typeof ErrorCode.AttestationMissing
  | typeof ErrorCode.AttestationNameMismatch
  | typeof ErrorCode.AttestationReasonMissing;

export class FileReportAttestationError extends Error {
  readonly code: AttestationErrorCode;
  constructor(
    code: AttestationErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'FileReportAttestationError';
    this.code = code;
  }
}

const filingPreflight = defineQuery({
  key: (reportId: string) => ['finance', 'reports', 'preflight', reportId],
  reads: 'GET /api/finance/reports/{id}/preflight',
  // Every modal open asks again, so the warning set matches what the workspace just changed.
  staleTime: 0,
  fetch: async (reportId, { signal, campaignId }): Promise<PreflightResult | null> => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/preflight`, {
      signal,
      headers: campaignHeaders(campaignId),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch filing preflight');
    return response.json() as Promise<PreflightResult>;
  },
});

/**
 * FPD-005 — the alert-don't-block preflight for a report: hard blockers, warnings, the fee lines.
 * `null` when the report isn't in the current campaign; idle until there is a report to ask about.
 */
export function useFilingPreflight(reportId: string | null): CurrentRead<PreflightResult | null> {
  return filingPreflight(reportId ?? skip);
}

/** Refetches a report's preflight, for a Retry or Refresh the treasurer presses. */
export function useRefreshFilingPreflight(): (reportId: string) => Promise<void> {
  const refresh = useRefresh();
  return useCallback((reportId: string) => refresh(filingPreflight, reportId), [refresh]);
}

/**
 * FPD-013 — frozen signing-evidence read for the "Why is this here?" panel.
 * Returns null when the report has no SubmissionPackage (Ready report) or
 * the report isn't in the current campaign. The payload is frozen at file
 * time so a long staleTime is safe; the queryKey already isolates per
 * report id + campaign.
 */
const signingEvidenceQuery = defineQuery({
  key: (reportId: string) => ['finance', 'reports', 'signing-evidence', reportId],
  reads: 'GET /api/finance/reports/{id}/signing-evidence',
  staleTime: 5 * 60_000,
  fetch: async (reportId, { signal, campaignId }): Promise<SigningEvidenceDto | null> => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/signing-evidence`, {
      signal,
      headers: campaignHeaders(campaignId),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch signing evidence');
    return response.json() as Promise<SigningEvidenceDto>;
  },
});

export function useSigningEvidence(reportId: string | null): CurrentRead<SigningEvidenceDto | null> {
  return signingEvidenceQuery(reportId ?? skip);
}

/**
 * FPD-024 — amendment-chain enumeration for the filing-version picker. The
 * picker mounts on the "Why is this here?" panel (FPD-013) and the
 * audit-packet download UI (FPD-026); both share this cache so switching
 * surfaces re-uses the same chain without a refetch. A write that changes it names
 * the report or its package, which refreshes it, so a long staleTime is correct.
 */
const filingChainQuery = defineQuery({
  key: (reportId: string) => ['finance', 'reports', 'filing-chain', reportId],
  reads: 'GET /api/finance/reports/{id}/chain',
  staleTime: 5 * 60_000,
  fetch: async (reportId, { signal, campaignId }): Promise<FilingChainDto | null> => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/chain`, {
      signal,
      headers: campaignHeaders(campaignId),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch filing chain');
    return response.json() as Promise<FilingChainDto>;
  },
});

export function useFilingChain(reportId: string | null): CurrentRead<FilingChainDto | null> {
  return filingChainQuery(reportId ?? skip);
}

/**
 * FPD-020 — Confirm-filing modal's effective-count preview. Runs the same
 * inclusion gate the file-time builder will, so what the modal shows is what
 * the file actually contains. Returns the contribution / expenditure / refund
 * totals + `attestedPendingCount` + `excludedDueToStatusDrift`.
 *
 * Wired as a `useMutation` rather than `useQuery` because the in-flight
 * `WarningDispositions` change as the treasurer attests rows; each attest
 * round-trips for the latest count. Re-running this hook with stale dispositions
 * would falsify the modal totals.
 */
export function usePreviewWithDispositions() {
  const { mutateAsync } = useMutation({
    mutationFn: async (vars: {
      reportId: string;
      warningDispositions: WarningDispositionInput[];
    }) => {
      const response = await apiResponse(
        `/api/finance/reports/${vars.reportId}/preview-with-dispositions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          json: { warningDispositions: vars.warningDispositions },
        },
      );
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to preview filing with dispositions');
      return (await response.json()) as EffectivePreviewDto;
    },
  });
  return { previewWithDispositions: mutateAsync };
}

const filingPreviewQuery = defineQuery({
  key: (reportId: string) => ['finance', 'reports', 'filing-preview', reportId],
  reads: 'GET /api/finance/reports/{id}/preview',
  // Each modal open asks again.
  staleTime: 0,
  fetch: async (reportId, { signal, campaignId }): Promise<FilingPreviewDto | null> => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/preview`, {
      signal,
      headers: campaignHeaders(campaignId),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch filing preview');
    return response.json() as Promise<FilingPreviewDto>;
  },
});

export function useFilingPreview(reportId: string | null): CurrentRead<FilingPreviewDto | null> {
  return filingPreviewQuery(reportId ?? skip);
}

/** Refetches a report's filing preview, for the Refresh the treasurer presses. */
export function useRefreshFilingPreview(): (reportId: string) => Promise<void> {
  const refresh = useRefresh();
  return useCallback((reportId: string) => refresh(filingPreviewQuery, reportId), [refresh]);
}

// ============================================================================
// Amend Report
// ============================================================================

/**
 * Resolves to the id of the report to open next: the new amendment, the amendment of this report
 * already in progress, or the current version of a report a filed amendment superseded. The last
 * two are 409 refusals the server names a report for, so the treasurer lands on it either way. A
 * report that changed since the caller read it (a stale `concurrencyVersion`) fails with the server's
 * refresh message.
 */
const useAmendReportWrite = defineWrite({
  send: async ({ reportId, concurrencyVersion }: { reportId: string; concurrencyVersion: number }): Promise<string> => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/amend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ concurrencyVersion } satisfies AmendReportRequest),
    });
    if (!response.ok) {
      // Surface the server's guidance verbatim — for a portal-only jurisdiction (AL) the amend
      // endpoint returns "…make corrections in the portal", which is exactly the treasurer-facing
      // next step the amendment-first affordance shows (alert-don't-block, ADR-064). Fall back to a
      // generic message when the body isn't JSON.
      const err =
        typeof response.json === 'function' ? await response.json().catch(() => null) : null;
      if (err?.code === ErrorCode.AmendmentInProgress) return err.amendmentReportId as string;
      if (err?.code === ErrorCode.SupersededReport) return err.currentReportId as string;
      if (isStaleWrite(err)) throw failureFromResponse(response, err);
      throw new Error(err?.error ?? err?.message ?? 'Failed to amend report');
    }
    return ((await response.json()) as AmendReportResult).report.id;
  },
  // The answer is a report id, not a token, so this is the plain arm; a lost race still refetches the
  // report so a retry sends its fresh token.
  staleRow: ({ reportId }) => ({ entity: 'ComplianceReport', id: reportId }),
});

export function useAmendReport() {
  const { mutateAsync } = useAmendReportWrite();
  return { amendReport: mutateAsync };
}

// ============================================================================
// Period unlock / relock (ADR-019) — the easy-but-audited manual reopen of a
// calendar-locked period. POST /api/finance/period-lock/{unlock,relock}; the
// report-detail reopen affordance sends `{ complianceReportId, reason }` (the
// backend resolves the right period key — covering deadline for in-system, the
// report itself for an externally-filed backfill). A reason is required. On
// success the write set refreshes the list, detail, advisory and chain, so the lock badge /
// "reopened" banner show without a manual reload.
// ============================================================================

function mutatePeriodLock(
  path: 'unlock' | 'relock',
  request: PeriodLockMutationRequest,
): Promise<PeriodLockMutationResult> {
  return apiJson<PeriodLockMutationResult>(`/api/finance/period-lock/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    json: request,
    // The PeriodLockController returns the bare message string (BadRequest/NotFound),
    // so the parsed body is a string; surface it, else fall back to a generic.
    error: (body: unknown) =>
      new Error(typeof body === 'string' && body ? body : 'Failed to update the period lock.'),
  });
}

const usePeriodLockMutationWrite = defineWrite({
  send: ({ path, request }: { path: 'unlock' | 'relock'; request: PeriodLockMutationRequest }) =>
    mutatePeriodLock(path, request),
});

export function usePeriodLockMutation() {
  const { mutateAsync, isPending } = usePeriodLockMutationWrite();

  // mutateAsync is reference-stable; the wrappers are memoized so they're safe in
  // dep arrays (Golden Rule — destructure the stable mutate function).
  const unlockPeriod = useCallback(
    (request: PeriodLockMutationRequest) => mutateAsync({ path: 'unlock', request }),
    [mutateAsync],
  );
  const relockPeriod = useCallback(
    (request: PeriodLockMutationRequest) => mutateAsync({ path: 'relock', request }),
    [mutateAsync],
  );

  return { unlockPeriod, relockPeriod, isPending };
}

// USI-005 (ADR-011): FPD-015 reconciliation report retired alongside the
// backend service (deleted in USI-004). The `useReconciliationReport`
// hook + the corresponding page surface are gone.

// ============================================================================
// Obligation report generate / mark-reported — the OBLIGATION-GENERIC pair
// (GitHub #164). The `/generate` and `/mark-reported` endpoints serve both the
// AL §17-5-8.1(c) rapid report (per-contribution, contribution-flipping) AND the
// VA §24.2-947.11 pre-session report (whole-window multi-donor, supplemental,
// non-flipping — ADR-039). Server-state-driven via FilingObligation.generatedAt
// so the two-state control persists across sessions:
//   • POST /obligations/{id}/generate returns the EDI/COMET file (application/xml
//     + Content-Disposition filename) and stamps GeneratedAt → the control flips
//     to "Mark as reported".
//   • POST /obligations/{id}/mark-reported marks the obligation → Filed (the row
//     disappears). Whether it flips the contribution(s) is the obligation's
//     concern (rapid: yes; pre-session: no — `contributionFlipped: false`).
// Invalidation refreshes the obligations query (both kinds) + the pre-session
// preview (so a re-opened modal reflects the new state).
// ============================================================================

/**
 * A typed obligation-generate failure carrying the server's stable `code`
 * so the pre-session modal can translate the named refusals
 * (`PreSessionSessionDateNotConfirmed`, `PreSessionWindowStillOpen`,
 * `PreSessionNoQualifyingDonors`, plus the shared rapid codes) to plain treasurer
 * copy instead of leaking an engineer identifier. Mirrors `MarkFiledInPortalError`.
 */
export class ObligationGenerateError extends Error {
  constructor(public readonly code: string | null, message: string) {
    super(message);
    this.name = 'ObligationGenerateError';
  }
}

export interface ObligationGenerateResult {
  fileBlob: Blob;
  fileName: string;
  obligationId: string;
}

export function useGenerateObligationReport() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: async (obligationId: string): Promise<ObligationGenerateResult> => {
      const response = await authService.authenticatedFetch(
        `${API_BASE_URL}/api/finance/obligations/${obligationId}/generate`,
        { method: 'POST' },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        // Preserve the stable error code so the pre-session modal can map it to
        // code-specific copy; the message is the human fallback for other callers.
        throw new ObligationGenerateError(
          (err?.code as string | undefined) ?? null,
          err?.error ?? err?.message ?? 'Failed to generate the report file',
        );
      }
      const fileBlob = await response.blob();
      // The real filename comes from Content-Disposition; the fallback only fires if
      // the server omits it (mirrors the sibling download hooks).
      const fallback = `obligation-report-${obligationId}.xml`;
      const fileName = readDispositionFileName(response.headers.get('Content-Disposition'), fallback);
      return { fileBlob, fileName, obligationId };
    },
    onSuccess: (result) => {
      // GeneratedAt is now stamped → the deck control flips to "Mark as reported".
      // Refresh the pre-session preview too so a re-opened modal isn't stale.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.finance.preSessionPreview(result.obligationId),
      });
    },
  });

  return { generateObligationReport: mutateAsync };
}

const useMarkObligationReportedWrite = defineWrite({
  send: (obligationId: string): Promise<RapidReportMarkReportedResponse> =>
    apiJson<RapidReportMarkReportedResponse>(`/api/finance/obligations/${obligationId}/mark-reported`, {
      method: 'POST',
      error: sentenceOr('Failed to mark the report reported'),
    }),
});

export function useMarkObligationReported() {
  const { mutateAsync } = useMarkObligationReportedWrite();

  return { markObligationReported: mutateAsync };
}

// ============================================================================
// VA §24.2-947.11 pre-session report (GitHub #164, ADR-039) — the modal's
// preview read + the confirm-session-date attestation.
//   • GET  /obligations/{id}/pre-session-preview — the assembly plan the modal
//     renders (window, session date, blocking gaps, excluded gifts, loan alerts,
//     canGenerate). Fetched on modal open; staleTime 0 so each open is fresh.
//   • POST /obligations/{id}/confirm-session-date — writes the audited session-date
//     attestation (required before generate). Invalidates the preview because the
//     window re-scopes off the confirmed date (windowEnd = sessionStart − 1 day).
// ============================================================================

export function usePreSessionPreview(obligationId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.preSessionPreview(obligationId ?? ''),
    queryFn: async () => {
      const response = await apiResponse(`/api/finance/obligations/${obligationId}/pre-session-preview`);
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch the pre-session report preview');
      return response.json() as Promise<PreSessionReportPreview>;
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!obligationId,
    // The counts + window must reflect the live confirmed date each open.
    staleTime: 0,
  });
}

export function useConfirmSessionDate() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: ({
      obligationId,
      sessionStartDate,
    }: {
      obligationId: string;
      // Omit to accept the computed prefill (second Wednesday of January).
      sessionStartDate?: string;
    }): Promise<PreSessionSessionDateConfirmResult> =>
      apiJson<PreSessionSessionDateConfirmResult>(
        `/api/finance/obligations/${obligationId}/confirm-session-date`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          json: sessionStartDate ? { sessionStartDate } : {},
          error: sentenceOr('Failed to confirm the session date'),
        },
      ),
    onSuccess: (result) => {
      // The window re-scopes off the confirmed date — refetch the preview so the
      // counts + windowClosed reflect the new bounds.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.finance.preSessionPreview(result.obligationId),
      });
    },
  });

  return { confirmSessionDate: mutateAsync };
}

// ============================================================================
// Daily report (§17-5-8(b)(3) daily aggregate, GitHub #81) — the daily-obligation
// strip's single "Mark filed in portal" action. POST
// /obligations/{id}/mark-filed flips the DailyAggregate obligation → Filed (the
// row disappears). Unlike the rapid report there's no Generate step — the
// treasurer files the daily report directly in the FCPA portal, then marks it
// filed here. Invalidates the obligations + health queries so the strip reflects
// the cleared obligation.
// ============================================================================

export interface DailyReportMarkFiledResult {
  obligationId: string;
  status: string;
}

const useMarkDailyReportFiledWrite = defineWrite({
  send: (obligationId: string): Promise<DailyReportMarkFiledResult> =>
    apiJson<DailyReportMarkFiledResult>(`/api/finance/obligations/${obligationId}/mark-filed`, {
      method: 'POST',
      error: sentenceOr('Failed to mark the daily report filed'),
    }),
});

export function useMarkDailyReportFiled() {
  const { mutateAsync } = useMarkDailyReportFiledWrite();

  return { markDailyReportFiled: mutateAsync };
}
